import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

const BRANDS = ["All", "AREL", "Daikin"];

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "all";
  const initialBrand = searchParams.get("brand") || "All";
  const [category, setCategory] = useState(initialCategory);
  const [brand, setBrand] = useState(initialBrand);
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category && category !== "all") params.append("category", category);
    if (brand && brand !== "All") params.append("brand", brand);
    if (query) params.append("q", query);
    api
      .get(`/products?${params.toString()}`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category, brand, query]);

  useEffect(() => {
    const params = {};
    if (category && category !== "all") params.category = category;
    if (brand && brand !== "All") params.brand = brand;
    setSearchParams(params);
     
  }, [category, brand]);

  const activeCat = useMemo(
    () => categories.find((c) => c.slug === category),
    [category, categories]
  );

  return (
    <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10" data-testid="products-page">
      <div className="mb-12">
        <div className="overline mb-4">Product Catalog</div>
        <h1 className="font-display text-5xl font-black tracking-tight text-ink md:text-6xl">
          {activeCat ? activeCat.name : "All Products"}
        </h1>
        <p className="mt-4 max-w-xl text-muted-warm">
          {activeCat?.description ||
            "Browse the full AREL and Daikin range — Smart LED TVs, air conditioners, washing machines, chest freezers and air coolers."}
        </p>
      </div>

      <div className="mb-10 grid gap-6 md:grid-cols-[280px,1fr] md:gap-10">
        <aside className="space-y-8" data-testid="products-filter-sidebar">
          <div>
            <div className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-warm">
              <SlidersHorizontal className="h-4 w-4" /> Brand
            </div>
            <div className="flex flex-wrap gap-2">
              {BRANDS.map((b) => (
                <button
                  key={b}
                  data-testid={`filter-brand-${b.toLowerCase()}`}
                  onClick={() => setBrand(b)}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors duration-200 ease-out ${
                    brand === b
                      ? b === "Daikin"
                        ? "bg-[#0097e0] text-white"
                        : "bg-[#dd0447] text-white"
                      : "border border-line-strong text-ink hover:bg-[#f4f1ec]"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-warm">
              <SlidersHorizontal className="h-4 w-4" /> Categories
            </div>
            <div className="space-y-1">
              <button
                data-testid="filter-category-all"
                onClick={() => setCategory("all")}
                className={`w-full rounded-md px-4 py-2.5 text-left text-sm transition-colors duration-200 ease-out ${
                  category === "all"
                    ? "bg-[#dd0447] text-white"
                    : "text-ink hover:bg-[#f4f1ec]"
                }`}
              >
                All Products
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  data-testid={`filter-category-${c.slug}`}
                  onClick={() => setCategory(c.slug)}
                  className={`w-full rounded-md px-4 py-2.5 text-left text-sm transition-colors duration-200 ease-out ${
                    category === c.slug
                      ? "bg-[#dd0447] text-white"
                      : "text-ink hover:bg-[#f4f1ec]"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <div>
          <div className="mb-8 flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8a857c]" />
              <input
                data-testid="products-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products by name or model..."
                className="arel-input w-full rounded-full py-3 pl-11 pr-4 text-sm"
              />
            </div>
            <div className="text-sm text-muted-warm">
              {products.length} result{products.length !== 1 ? "s" : ""}
            </div>
          </div>

          {loading ? (
            <div className="py-24 text-center text-muted-warm" data-testid="products-loading">
              Loading products...
            </div>
          ) : products.length === 0 ? (
            <div className="py-24 text-center text-muted-warm" data-testid="products-empty">
              No products found. Try a different filter.
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {products.map((p) => {
                const isDaikin = p.brand === "Daikin";
                return (
                  <Link
                    key={p.id}
                    to={`/products/${p.id}`}
                    data-testid={`product-card-${p.id}`}
                    className="arel-card group flex flex-col overflow-hidden rounded-xl"
                  >
                    <div className={`relative aspect-square overflow-hidden ${isDaikin ? "bg-gradient-to-br from-[#e6f6fe] to-white" : "bg-[#efe9df]"}`}>
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                      />
                      <div className={`absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white ${isDaikin ? "bg-[#0097e0]" : "bg-[#dd0447]"}`}>
                        {p.brand || "AREL"}
                      </div>
                      {p.featured && !isDaikin && (
                        <div className="absolute right-4 top-4 rounded-full bg-[#dd0447] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                          Featured
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col gap-2 p-5">
                      <div className={`overline ${isDaikin ? "text-[#0097e0]" : ""}`}>{p.model || p.brand}</div>
                      <div className="font-display text-lg font-bold leading-tight text-ink">
                        {p.name}
                      </div>
                      <div className="text-sm text-muted-warm line-clamp-2">
                        {p.short_description}
                      </div>
                      <div className={`mt-auto pt-3 inline-flex items-center gap-2 text-sm font-semibold ${isDaikin ? "text-[#0097e0]" : "text-[#dd0447]"}`}>
                        View details <ArrowUpRight className="h-4 w-4" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
